import { EntityTypeEnum } from "@app/projects/enumerators";
import * as React from "react";
import { Link } from "react-router-dom";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";
import { AccuracyProvider } from "@app/projects/results/accuracy/accuracyProvider";
import { AccuracyProviderDetail } from "@app/projects/results/accuracy/accuracyProviderDetail";
import { AccuracyProviderInspectionProfile } from "@app/projects/servicing-provider/accuracy/AccuracyProviderInspectionProfile";
import { getEntityIcon, getEntityTypeDescription } from "@app/projects/servicing-provider/ServicingProviderHelpers";
import { formatDateCustom, formatUnconvertedDateCustom } from "@app/shared/formatDateTime";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

interface AccuracyProviderProfileProps {
  provider: AccuracyProvider;
  providerNetworkType: ProviderNetworkTypeEnum;
  showAttestation: boolean;
  runConfigurationDate: Date;
}


interface AccuracyProviderProfileState {
  providerDetail: AccuracyProviderDetail;
}

class AccuracyProviderProfile extends React.Component<AccuracyProviderProfileProps & FeatureFlagContextProps, AccuracyProviderProfileState> {
  constructor(props: AccuracyProviderProfileProps & FeatureFlagContextProps) {
    super(props);
    this.state = {
      providerDetail: props.provider as AccuracyProviderDetail,
    };
  }

  public render() {
    const providerDetail = this.state.providerDetail;

    return (
      <div className="servicing-provider-profile">
        <section>
          <div className="row">
            <div className="col-sm-2">
              <h1 className="text-center">
                <QesIcon iconName={getEntityIcon(providerDetail.entityType)} description={getEntityTypeDescription(providerDetail.entityType)} className="text-very-large" />
              </h1>
            </div>
            <div className="col-sm-8">
              <h3 className="pl0-imp mb-0">
                {providerDetail.displayName}
                {providerDetail.credentials && !providerDetail.displayName.includes(providerDetail.credentials) ? `, ${providerDetail.credentials}` : ""}
              </h3>
              {providerDetail.entityType ?
                <div>
                  <strong className="text-muted">{getEntityTypeDescription(providerDetail.entityType)}</strong>
                  < br />
                </div> :
                null
              }
              {providerDetail.address ? <>{providerDetail.address}<br /></> : null}
              {providerDetail.address2 ? <>{providerDetail.address2}<br /></> : null}
              {providerDetail.city}
              {providerDetail.city && (providerDetail.state || providerDetail.zip) ? ", " : null}
              {providerDetail.state} {providerDetail.zip}<br />
              {providerDetail.recommendedAction ?
                <div>
                  <strong>Recommended Action</strong> {providerDetail.recommendedAction.actionType}<br />
                  <strong>Reason</strong> {providerDetail.recommendedAction.reasons ? providerDetail.recommendedAction.reasons.join("; ") : ""}
                </div> :
                null
              }
              {this.renderAttestationStatus(providerDetail.lastAttestedDate)}
              <RoleRestricted allowedRoles={[Roles.ACCURACY_EVIDENCE_VIEWER]}>
                {this.renderEvidenceViewerLink(providerDetail)}
              </RoleRestricted>
            </div>
            <div className="col-sm-2 px-0">
              <div className="text-success mb-1">
                <QesIcon iconName={QesIcons.Check} description="Valid" className="ms-2 me-1 align-text-top" /> = Valid
              </div>
              <div className="text-danger mb-1">
                <QesIcon iconName={QesIcons.X} description="Invalid" className="ms-2 me-1 align-text-top" /> = Invalid
              </div>
              <div className="text-info mb-1">
                <QesIcon iconName={QesIcons.StarV1} description="Enriched" className="ms-2 me-1 align-text-top" /> = Enriched
              </div>
              <div className="text-muted">
                <QesIcon iconName={QesIcons.Help} description="Unknown" className="ms-2 me-1 align-text-top" /> = Unknown
              </div>
            </div>
          </div>
        </section>
        <section className="mb-0">
          <div className="row">
            <div className="col-12">
              <div className="tabs-style-2">
                <div className="tab-content">
                  {this.getTabContent(providerDetail)}
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  private renderEvidenceViewerLink(providerDetails: AccuracyProviderDetail) {
    if (providerDetails.entityType && providerDetails.entityType == EntityTypeEnum.Facility.toLowerCase()) {
      return this.renderEvidenceViewerLinkForFacilities(providerDetails);
    }

    return this.renderEvidenceViewerLinkForPractitioners(providerDetails);
  }

  private renderEvidenceViewerLinkForFacilities(providerDetails: AccuracyProviderDetail) {
    const linkWithAddress = <Link to={`/tools/accuracyevidence?address1=${providerDetails.address}&address2=${providerDetails.address2 ? providerDetails.address2 : ""}&city=${providerDetails.city}&state=${providerDetails.state}&zip=${providerDetails.zip}&temporalPosition=${formatDateCustom(this.props.runConfigurationDate, "YYYY-MM-DDTHH:mm:ss.SSS")}`} target="_blank">Evidence</Link>;
    const linkWithXid = <Link to={`/tools/accuracyevidence?xid=${providerDetails.slXid}&temporalPosition=${formatDateCustom(this.props.runConfigurationDate, "YYYY-MM-DDTHH:mm:ss.SSS")}`} target="_blank">Evidence</Link>

    return (providerDetails.slXid && this.props.enabledFeatureFlags.has(Features.USE_XIDS_IN_QES_ACCURACY))
      ? linkWithXid
      : linkWithAddress;
  }

  private renderEvidenceViewerLinkForPractitioners(providerDetails: AccuracyProviderDetail) {

    const linkWithNPI = <Link to={`/tools/accuracyevidence?npi=${providerDetails.npi}&temporalPosition=${formatDateCustom(this.props.runConfigurationDate, "YYYY-MM-DDTHH:mm:ss.SSS")}`} target="_blank">Evidence</Link>;
    const linkWithXid = <Link to={`/tools/accuracyevidence?xid=${providerDetails.hcpXid}&temporalPosition=${formatDateCustom(this.props.runConfigurationDate, "YYYY-MM-DDTHH:mm:ss.SSS")}`} target="_blank">Evidence</Link>

    return (providerDetails.hcpXid && this.props.enabledFeatureFlags.has(Features.USE_XIDS_IN_QES_ACCURACY))
      ? linkWithXid
      : linkWithNPI;
  }

  private getTabContent = (providerDetail: AccuracyProviderDetail) => {
    return (<AccuracyProviderInspectionProfile {...this.props} provider={providerDetail} />);
  }

  private renderAttestationStatus(lastAttestedDate?: Date) {
    if (!this.props.showAttestation) {
      return;
    }

    return (
      <div>
        <strong>Last Attested Date</strong> {(lastAttestedDate && this.state.providerDetail.isRecentlyAttested) ? formatUnconvertedDateCustom(lastAttestedDate, "L") : "---"}<br />
      </div>
    );

  }
}

const wrappedAccuracyProviderProfile = withFeatureFlagContext(AccuracyProviderProfile);

export { wrappedAccuracyProviderProfile as AccuracyProviderProfile };